import { IPageView } from "../models/server/meta";

export const PAGES_VIEWED_COOKIE = "pagesViewed";
export const PAGES_VIEWED_STORAGE_KEY = "pagesViewed";
export const MAX_PAGES_VIEWED = 200;
export const MAX_PAGE_PATH_LENGTH = 200;

export interface PagesViewedHistory {
    orderRef?: string;
    pages: IPageView[];
}

/** Browsers drop cookies above ~4KB, so the serialized value is kept below this. */
const MAX_COOKIE_BYTES = 3500;

const VALID_PAGE_VIEW_TIME = /^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/;

const pad = (value: number): string => String(value).padStart(2, "0");

export const formatPageViewTime = (date: Date): string =>
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;

export const isValidPageView = (value: unknown): value is IPageView => {
    const candidate = value as Partial<IPageView> | null;
    return (
        typeof candidate === "object" &&
        candidate !== null &&
        typeof candidate.page === "string" &&
        candidate.page.length > 0 &&
        candidate.page.length <= MAX_PAGE_PATH_LENGTH &&
        typeof candidate.time === "string" &&
        VALID_PAGE_VIEW_TIME.test(candidate.time)
    );
};

/** Parses the client-controlled cookie value. Anything unexpected is discarded rather than trusted. */
export function parsePagesViewed(rawCookieValue: string | undefined): PagesViewedHistory {
    if (!rawCookieValue) {
        return { pages: [] };
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(rawCookieValue);
    } catch {
        return { pages: [] };
    }

    if (Array.isArray(parsed)) {
        return { pages: parsePageViewList(parsed) };
    }

    if (typeof parsed !== "object" || parsed === null || !Array.isArray((parsed as PagesViewedHistory).pages)) {
        return { pages: [] };
    }

    const history = parsed as PagesViewedHistory;
    return {
        ...(typeof history.orderRef === "string" && history.orderRef.length > 0 ? { orderRef: history.orderRef } : {}),
        pages: parsePageViewList(history.pages),
    };
}

function parsePageViewList(pages: unknown[]): IPageView[] {
    return pages
        .filter(isValidPageView)
        .map(({ page, time }) => ({ page, time }))
        .slice(0, MAX_PAGES_VIEWED);
}

export function serializePagesViewed(pages: IPageView[], orderRef?: string): string {
    const capped = pages.slice(-MAX_PAGES_VIEWED);
    let firstIndex = 0;
    let value = JSON.stringify({ orderRef, pages: capped });

    while (encodeURIComponent(value).length > MAX_COOKIE_BYTES && firstIndex < capped.length) {
        firstIndex++;
        value = JSON.stringify({ orderRef, pages: capped.slice(firstIndex) });
    }

    return value;
}
